/** @file readEvents: clip events of design 17.9; step and hook events need a node and a mode. */
import type { ClipEvent } from '@zilo/pose3d-format/types/clip';
import { checkEnum } from '@zilo/pose3d-format/validate/fields/checkEnum';
import { checkString } from '@zilo/pose3d-format/validate/fields/checkString';
import { checkTuple } from '@zilo/pose3d-format/validate/fields/checkTuple';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { NAME_PATTERN } from '@zilo/pose3d-format/validate/fields/namePattern';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';

const TYPES = ['step', 'hook1', 'hook2', 'hook3', 'hit', 'sound', 'custom'] as const;
const MODES = ['fixed', 'follow', 'stop'] as const;
const PLANTED = new Set<string>(['step', 'hook1', 'hook2', 'hook3']);
const nodeName = checkString(NAME_PATTERN);
const typeCheck = checkEnum(TYPES);

function readEvent(value: unknown, path: string): ClipEvent {
  const r = readInput(value, path, ['type', 'node', 'mode', 'pos', 'payload']);
  const type = r.check('type', typeCheck) as ClipEvent['type'];
  if (PLANTED.has(type) && !r.has('node')) failField(r.at('node'), `a node (${type} events name one)`, undefined);
  if (PLANTED.has(type) && !r.has('mode')) failField(r.at('mode'), `one of ${MODES.join(', ')} (${type} events need a mode)`, undefined);
  return compact({
    type, node: r.opt('node', (k) => r.check(k, nodeName) as string), mode: r.opt('mode', (k) => r.oneOf(k, MODES)),
    pos: r.opt('pos', (k) => r.check(k, checkTuple(3)) as [number, number, number]), payload: r.opt('payload', r.raw),
  });
}

/** `value` as `ClipEvent[]`; `VALIDATION` at `<path>/<i>/<field>` for a bad type, mode, node or pos. */
export function readEvents(value: unknown, path: string): ClipEvent[] {
  if (!Array.isArray(value)) return failField(path, 'array of events', value);
  return value.map((e, i) => readEvent(e, `${path}/${String(i)}`));
}
